// src/components/PdfPages.jsx
// A fax's pages drawn as pictures (3.54.2). Phones can't show a PDF inside a
// page: Android's Chrome shows only a grey "PDF · Open" box (the owner's
// screenshot, Oct 11) and iPhones show part of it. So on phones the fax
// panel draws each page here, in the browser, from the file it has already
// downloaded (pdf.js, Mozilla's PDF reader, loaded only when needed; its
// worker comes from our own site, public/pdfjs). Nothing is sent anywhere.
// Computers keep the browser's own PDF viewer (zoom, print).
import { useEffect, useRef, useState } from 'react';
import Loader from './Loader';
import Emo from './Emo';

const MAX_PAGES = 40;          // a longer fax: the first 40, then Download
let pdfjsP = null;
function pdfjs() {
  if (!pdfjsP) {
    pdfjsP = import('pdfjs-dist/legacy/build/pdf').then(lib => {
      lib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdfjs/pdf.worker.min.js`;
      return lib;
    }).catch(e => { pdfjsP = null; throw e; });
  }
  return pdfjsP;
}

// 3.64.0: page 1 of a PDF as a picture (a data URL), for Compose's Send
// Confirmation; null when it can't be read (e.g. a password-protected file)
export async function firstPageImage(blob, cssWidth = 96) {
  let doc = null;
  try {
    const lib = await pdfjs();
    doc = await lib.getDocument({ data: new Uint8Array(await blob.arrayBuffer()), isEvalSupported: false, verbosity: 0 }).promise;
    const page = await doc.getPage(1);
    const ratio = Math.min(2, window.devicePixelRatio || 1);
    const viewport = page.getViewport({ scale: (cssWidth * ratio) / page.getViewport({ scale: 1 }).width });
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport }).promise;
    return canvas.toDataURL('image/png');
  } catch (e) {
    console.warn('[firstPageImage]', e?.message);
    return null;
  } finally { doc?.destroy?.(); }
}

// Phones and tablets that can't show a PDF in a frame
export const needsPdfPages = () => typeof navigator !== 'undefined'
  && (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '') || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

export default function PdfPages({ blob, fileName }) {
  const boxRef = useRef(null);
  const [state, setState] = useState({ phase: 'loading', pages: 0, shown: 0 });

  useEffect(() => {
    let cancelled = false, doc = null;
    const box = boxRef.current;
    (async () => {
      try {
        const lib = await pdfjs();
        const data = new Uint8Array(await blob.arrayBuffer());
        doc = await lib.getDocument({ data, isEvalSupported: false, disableFontFace: false, verbosity: 0 }).promise;
        if (cancelled) return;
        const total = doc.numPages, count = Math.min(total, MAX_PAGES);
        setState({ phase: 'drawing', pages: total, shown: 0 });
        // as wide as the panel, sharp on the phone's screen (at most 2× to save memory)
        const cssWidth = Math.max(240, (box?.clientWidth || 360) - 2);
        const ratio = Math.min(2, window.devicePixelRatio || 1);
        for (let n = 1; n <= count; n++) {
          const page = await doc.getPage(n);
          if (cancelled) return;
          const base = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: (cssWidth * ratio) / base.width });
          const canvas = document.createElement('canvas');
          canvas.width = Math.ceil(viewport.width);
          canvas.height = Math.ceil(viewport.height);
          canvas.style.width = '100%';
          canvas.style.height = 'auto';
          canvas.style.display = 'block';
          canvas.setAttribute('role', 'img');
          canvas.setAttribute('aria-label', `Page ${n} of ${total} of ${fileName || 'the fax'}`);
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          await page.render({ canvasContext: ctx, viewport }).promise;
          page.cleanup();
          if (cancelled) return;
          const wrap = document.createElement('div');
          wrap.className = 'pdfp-page';
          wrap.appendChild(canvas);
          const label = document.createElement('div');
          label.className = 'pdfp-label';
          label.textContent = `Page ${n} of ${total}`;
          wrap.appendChild(label);
          box?.querySelector('.pdfp-pages')?.appendChild(wrap);
          setState(s => ({ ...s, shown: n }));
        }
        setState(s => ({ ...s, phase: 'done' }));
      } catch (e) {
        if (!cancelled) { console.error('[PdfPages]', e?.message); setState({ phase: 'error', pages: 0, shown: 0 }); }
      }
    })();
    return () => {
      cancelled = true;
      doc?.destroy?.();
      box?.querySelector('.pdfp-pages')?.replaceChildren();
    };
  }, [blob, fileName]);

  return (
    <div ref={boxRef} style={S.box}>
      <style>{CSS}</style>
      <div className="pdfp-pages" style={S.pages} />
      {(state.phase === 'loading' || (state.phase === 'drawing' && state.shown === 0)) && (
        <div style={S.wait}><Loader size="sm" label="Loading the fax…" /></div>
      )}
      {state.phase === 'drawing' && state.shown > 0 && <div style={S.note}>Loading page {state.shown + 1} of {Math.min(state.pages, MAX_PAGES)}…</div>}
      {state.phase === 'error' && <div style={S.err}><Emo e="⚠️" /> The pages couldn’t be shown here. Use <strong>Download</strong> or <strong>Open in new tab</strong> below.</div>}
      {state.phase === 'done' && state.pages > MAX_PAGES && <div style={S.note}>This fax has {state.pages} pages. The first {MAX_PAGES} show here; use Download for the rest.</div>}
    </div>
  );
}

const CSS = `
.pdfp-page{background:#fff;border-radius:6px;box-shadow:0 1px 4px rgb(0 0 0 / .18);overflow:hidden}
.pdfp-label{font-size:11px;color:#607d8b;background:#f5f7f8;text-align:center;padding:3px 0}
`;
const S = {
  // 3.54.3 (owner's screenshot): the pages take their full height in the panel, which scrolls as one;
  // a box with its own scroll was squeezed to a sliver when the fax had a long history below it
  box: { width: '100%', flexShrink: 0, boxSizing: 'border-box' },
  pages: { display: 'flex', flexDirection: 'column', gap: 10 },
  wait: { display: 'flex', justifyContent: 'center', padding: '40px 0' },
  err: { fontSize: 14, color: 'var(--t-37474f)', textAlign: 'center', padding: '40px 16px', lineHeight: 1.5 },
  note: { fontSize: 12, color: 'var(--t-78909c)', textAlign: 'center', padding: '8px 0' },
};
